import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { BffLoginHistory } from '@lucky7ventures/bff-types';
import { envIsCanada } from '@/constants/constants';

const PAGE_SIZE = 20;

interface LoginHistoryCardsProps {
  data: BffLoginHistory[];
}

export function LoginHistoryCards({ data }: LoginHistoryCardsProps): JSX.Element {
  const intl = useIntl();
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(data.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pageData = data.slice(startIndex, startIndex + PAGE_SIZE);

  return (
    <div className="space-y-3">
      {pageData.map(login => (
        <LoginHistoryCard key={login.token} login={login} />
      ))}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-40"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            {intl.formatMessage({ id: 'loginHistory.previous' })}
          </button>
          <span className="text-sm text-gray-500">
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-40"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            {intl.formatMessage({ id: 'loginHistory.next' })}
          </button>
        </div>
      )}
    </div>
  );
}

function LoginHistoryCard({ login }: { login: BffLoginHistory }): JSX.Element {
  const intl = useIntl();
  const isActive = !login.isInvalidated;

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-2">
        <span className="font-semibold text-gray-800">
          {new Date(login.dateCreated).toLocaleDateString()}
        </span>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
          {isActive
            ? intl.formatMessage({ id: 'loginHistory.active' })
            : intl.formatMessage({ id: 'loginHistory.ended' })}
        </span>
      </div>

      {/* Session Time */}
      <div className="mb-3">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {intl.formatMessage({ id: 'loginHistory.sessionTime' })}
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          <CardField
            label={intl.formatMessage({ id: 'history.loggedIn' })}
            value={new Date(login.dateCreated).toLocaleTimeString()}
          />
          <CardField
            label={intl.formatMessage({ id: 'history.loggedOut' })}
            value={login.isInvalidated ? new Date(login.lastAccess).toLocaleTimeString() : '-'}
            align="right"
          />
        </div>
      </div>

      {/* Activity - Canada only */}
      {envIsCanada() && (
        <>
          <div className="my-2 border-t border-gray-100" />
          <div className="mb-3">
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              {intl.formatMessage({ id: 'loginHistory.activity' })}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <ActivityTile
                label={intl.formatMessage({ id: 'history.totalWagered' })}
                value={login.sessionData?.TotalWagered ?? '-'}
              />
              <ActivityTile
                label={intl.formatMessage({ id: 'history.totalWon' })}
                value={login.sessionData?.TotalWon ?? '-'}
              />
              <ActivityTile
                label={intl.formatMessage({ id: 'history.totalLost' })}
                value={login.sessionData?.TotalLost ?? '-'}
              />
            </div>
          </div>
        </>
      )}

      {/* Balance - Canada only */}
      {envIsCanada() && (
        <>
          <div className="my-2 border-t border-gray-100" />
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              {intl.formatMessage({ id: 'loginHistory.balance' })}
              {login.sessionData?.Currency ? ` (${login.sessionData.Currency})` : ''}
            </div>
            <div className="grid grid-cols-3 gap-1.5 text-center">
              <div>
                <span className="block text-[10px] text-gray-400">
                  {intl.formatMessage({ id: 'history.balanceStart' })}
                </span>
                <span className="text-sm font-medium text-gray-800">
                  {login.sessionData?.BalanceStart ?? '-'}
                </span>
              </div>
              <div>
                <span className="block text-[10px] text-gray-400">
                  {intl.formatMessage({ id: 'history.balanceEnd' })}
                </span>
                <span className="text-sm font-medium text-gray-800">
                  {login.sessionData?.BalanceEnd ?? '-'}
                </span>
              </div>
              <div>
                <span className="block text-[10px] text-gray-400">
                  {intl.formatMessage({ id: 'history.netPosition' })}
                </span>
                <span className="text-sm font-medium text-gray-800">
                  {login.sessionData?.NetPosition ?? '-'}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CardField({
  label,
  value,
  align = 'left',
}: {
  label: string;
  value: string;
  align?: 'left' | 'right';
}): JSX.Element {
  return (
    <div className={align === 'right' ? 'text-right' : ''}>
      <span className="block text-[11px] text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-800">{value}</span>
    </div>
  );
}

function ActivityTile({ label, value }: { label: string; value: string | number }): JSX.Element {
  return (
    <div className="rounded-lg bg-gray-50 px-1 py-2 text-center">
      <span className="block text-[10px] text-gray-400">{label}</span>
      <span className="text-[13px] font-medium text-gray-800">{String(value)}</span>
    </div>
  );
}
